import { useEffect, useState } from 'react';
import { useVaultStore, AccessRequest } from '../../../store/vaultStore';
import { useAuthStore } from '../../../store/authStore';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import { Plus, Check, X, Ban } from 'lucide-react';
import toast from 'react-hot-toast';

const PERMS = ['view', 'reveal', 'edit', 'share', 'delete'];
const STATUS_VARIANT: Record<string, any> = {
  pending: 'warning', approved: 'success', rejected: 'destructive', cancelled: 'secondary', expired: 'muted',
};

export function AccessRequests() {
  const store = useVaultStore();
  const userId = useAuthStore((s) => s.user?.id);
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({ vaultId: '', itemId: '', permissions: ['view'], reason: '', requestedExpiry: '' });

  const load = async () => {
    setLoading(true);
    try { setRequests(await store.listRequests(statusFilter || undefined)); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Failed to load requests'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const togglePerm = (p: string) =>
    setForm((f: any) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x: string) => x !== p) : [...f.permissions, p] }));

  const submit = async () => {
    if (!form.vaultId.trim()) { toast.error('Vault ID is required'); return; }
    if (!form.reason.trim()) { toast.error('A reason is required'); return; }
    if (form.permissions.length === 0) { toast.error('Select at least one permission'); return; }
    setSaving(true);
    try {
      await store.createRequest({
        vaultId: form.vaultId.trim(),
        itemId: form.itemId.trim() || undefined,
        requestedPermissions: form.permissions,
        reason: form.reason.trim(),
        requestedExpiry: form.requestedExpiry || undefined,
      });
      toast.success('Request submitted');
      setOpen(false);
      setForm({ vaultId: '', itemId: '', permissions: ['view'], reason: '', requestedExpiry: '' });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to submit request');
    } finally { setSaving(false); }
  };

  const decide = async (r: AccessRequest, decision: 'approve' | 'reject' | 'cancel') => {
    try { await store.decideRequest(r.id, decision); toast.success(`Request ${decision}d`); load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || `Failed to ${decision}`); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Access requests</h1>
          <p className="text-muted-foreground">Request temporary access, or approve requests for vaults you manage.</p>
        </div>
        <Button variant="primary" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-2" /> New request</Button>
      </div>

      <div className="flex gap-1 border-b border-border">
        {['', 'pending', 'approved', 'rejected'].map((s) => (
          <button key={s || 'all'} onClick={() => setStatusFilter(s)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${
              statusFilter === s ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader><h3 className="text-foreground font-display text-section-title">Requests</h3></CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : requests.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">No requests.</div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-secondary/50 text-muted-foreground font-semibold border-y border-border">
                <tr>
                  <th className="px-6 py-3">Resource</th>
                  <th className="px-6 py-3">Permissions</th>
                  <th className="px-6 py-3">Reason</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.map((r) => {
                  const mine = r.requesterId === userId;
                  return (
                    <tr key={r.id} className="hover:bg-secondary/20">
                      <td className="px-6 py-4 font-mono text-xs">{r.itemId ? `item ${r.itemId.slice(0, 8)}…` : `vault ${r.vaultId.slice(0, 8)}…`}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {r.requestedPermissions.map((p) => <Badge key={p} variant="secondary">{p}</Badge>)}
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-xs truncate text-muted-foreground">{r.reason}</td>
                      <td className="px-6 py-4"><Badge variant={STATUS_VARIANT[r.status]}>{r.status.toUpperCase()}</Badge></td>
                      <td className="px-6 py-4 text-right">
                        {r.status === 'pending' && (
                          <div className="flex items-center justify-end gap-1">
                            {mine ? (
                              <Button variant="ghost" size="xs" onClick={() => decide(r, 'cancel')} className="gap-1"><Ban className="w-3.5 h-3.5" /> Cancel</Button>
                            ) : (
                              <>
                                <Button variant="ghost" size="xs" className="gap-1 text-success-strong" onClick={() => decide(r, 'approve')}><Check className="w-3.5 h-3.5" /> Approve</Button>
                                <Button variant="ghost" size="xs" className="gap-1 text-destructive" onClick={() => decide(r, 'reject')}><X className="w-3.5 h-3.5" /> Reject</Button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request access</DialogTitle>
            <DialogDescription>A manager of the vault must approve. You cannot approve your own request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Vault ID</label>
              <Input value={form.vaultId} onChange={(e) => setForm((f: any) => ({ ...f, vaultId: e.target.value }))} className="font-mono" placeholder="vault UUID" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Item ID <span className="text-muted-foreground">(optional)</span></label>
              <Input value={form.itemId} onChange={(e) => setForm((f: any) => ({ ...f, itemId: e.target.value }))} className="font-mono" placeholder="credential UUID (for item-level access)" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Permissions</label>
              <div className="flex flex-wrap gap-3">
                {PERMS.map((p) => (
                  <label key={p} className="flex items-center gap-1.5 text-sm capitalize">
                    <input type="checkbox" className="size-4 accent-primary" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} />
                    {p}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Reason</label>
              <Textarea rows={2} value={form.reason} onChange={(e) => setForm((f: any) => ({ ...f, reason: e.target.value }))} placeholder="Why do you need access?" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Expiry <span className="text-muted-foreground">(optional)</span></label>
              <Input type="datetime-local" value={form.requestedExpiry} onChange={(e) => setForm((f: any) => ({ ...f, requestedExpiry: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={submit} isLoading={saving}>Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
